/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Link,
  Toggle,
  PaginationNav,
  InlineNotification
} from 'carbon-components-react';
import { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import BotMessage from '@ibm-watson/shared/modules/actions/components/sample-conversation/BotMessage';
import ClientMessage from '@ibm-watson/shared/modules/actions/components/sample-conversation/ClientMessage';
import SampleConversationWidget from '@ibm-watson/shared/modules/actions/components/sample-conversation/SampleConversationWidget';
import SkillSettingsPreviewHeader from '@ibm-watson/shared/modules/ia/components/skill-settings-panel/SkillSettingsPreviewHeader';
import { useAppSelector } from '../../../store';
import { SEARCH_INTEGRATION_TYPES } from '../../../ts/skill';
import { setCustomServiceDraftIsConversationalSearchEnabled } from '../../custom-service/store/reducer';
import { selectInstanceRegion } from '../../ia/store/session/selectors';
import { isCpd, isWxoEnabled } from '../../ia/util/config';
import { generateDocsUrl } from '../../ia/util/docs';
import { Regions } from '../../ia/util/regions';
import { setDraftIsConversationalSearchEnabled as setMilvusDraftIsConversationalSearchEnabled } from '../../milvus/store/reducer';
import { setDraftIsConversationalSearchEnabled } from '../store/reducer';
import {
  ConversationalSearchMessages as messages,
  ConfigureContentMessages as configMessages
} from './messages';

import './ConversationalSearch.scss';

const conversationalSearchGALinks = {
  pricing: {
    ibmCloud: generateDocsUrl(
      'catalog/services/watsonx-assistant?catalog_query=aHR0cHM6Ly9jbG91ZC5pYm0uY29tL2NhdGFsb2c%2Fc2VhcmNoPXdhdHNvbnglMjUyMGFzc2lzdGFudCNzZWFyY2hfcmVzdWx0cw%3D%3D&planId=f0a3dd47-b693-4d73-a8df-aa6baf07a933'
    ),
    wxo: 'https://www.ibm.com/products/watsonx-orchestrate/pricing'
  },
  terms: {
    ibmCloud:
      'https://www.ibm.com/support/customer/csol/terms/?id=i128-0038&lc=en',
    wxo: 'https://www.ibm.com/support/customer/csol/terms/?ref=i126-9451-11-01-2024-zz-en'
  }
};

const ELASTICSEARCH_ADD_DOCS_URL =
  generateDocsUrl(
    '/watson-assistant?topic=watson-assistant-search-elasticsearch-add'
  ) + '#setup-elasticsearch';

interface ConversationalSearchProps {
  searchType: SEARCH_INTEGRATION_TYPES;
}

export const ConversationalSearch = ({
  searchType
}: ConversationalSearchProps) => {
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();
  const [previewPage, setPreviewPage] = useState(0);
  const [searchEnabled, setSearchEnabled] = useState(true);
  const instanceRegion = useAppSelector(selectInstanceRegion);
  const frankfurtCustomer = instanceRegion === Regions.EU_DE;

  const searchToggled = () => {
    switch (searchType) {
      case SEARCH_INTEGRATION_TYPES.CUSTOM_SERVICE:
        dispatch(
          setCustomServiceDraftIsConversationalSearchEnabled(!searchEnabled)
        );
        break;
      case SEARCH_INTEGRATION_TYPES.MILVUS:
        dispatch(setMilvusDraftIsConversationalSearchEnabled(!searchEnabled));
        break;
      case SEARCH_INTEGRATION_TYPES.ELASTIC:
      default:
        dispatch(setDraftIsConversationalSearchEnabled(!searchEnabled));
    }

    setSearchEnabled(!searchEnabled);
  };

  useEffect(() => {
    switch (searchType) {
      case SEARCH_INTEGRATION_TYPES.CUSTOM_SERVICE:
        dispatch(setCustomServiceDraftIsConversationalSearchEnabled(true));
        break;
      case SEARCH_INTEGRATION_TYPES.MILVUS:
        dispatch(setMilvusDraftIsConversationalSearchEnabled(true));
        break;
      case SEARCH_INTEGRATION_TYPES.ELASTIC:
      default:
        dispatch(setDraftIsConversationalSearchEnabled(true));
    }
  }, [dispatch, searchType]);

  const renderConversationalSearchDescription = () => {
    if (isCpd) {
      return <FormattedMessage {...messages.description2CPD} />;
    }

    if (frankfurtCustomer) {
      return <FormattedMessage {...messages.description2Frankfurt} />;
    }

    return <FormattedMessage {...messages.description2} />;
  };

  return (
    <div className="ConversationalSearch bx--row">
      <div className="bx--col-lg-8">
        <div className="ConversationalSearch__title">
          <FormattedMessage {...messages.title} />
        </div>
        <div className="ConversationalSearch__description">
          <FormattedMessage {...messages.description1} />
        </div>
        <div className="ConversationalSearch__description">
          {renderConversationalSearchDescription()}
          <Link
            target="_blank"
            rel="noopener noreferrer"
            className="ConversationalSearch__link"
            href={ELASTICSEARCH_ADD_DOCS_URL}>
            <FormattedMessage {...messages.learnMore} />
          </Link>
        </div>
        <Toggle
          id="conversational-search-toggle"
          labelA={t(messages.off)}
          labelB={t(messages.on)}
          toggled={searchEnabled}
          onToggle={searchToggled}
        />
        {!isCpd && (
          <InlineNotification
            lowContrast
            className="ConversationalSearch__pricing-notification"
            kind="info"
            subtitle={t(messages.pricingAndTermsInfo, {
              PricingLink: (
                <Link
                  href={
                    isWxoEnabled
                      ? conversationalSearchGALinks.pricing.wxo
                      : conversationalSearchGALinks.pricing.ibmCloud
                  }
                  target="_blank"
                  rel="noopener noreferrer">
                  {t(configMessages.pricing)}
                </Link>
              ),
              TermsLink: (
                <Link
                  href={
                    isWxoEnabled
                      ? conversationalSearchGALinks.terms.wxo
                      : conversationalSearchGALinks.terms.ibmCloud
                  }
                  target="_blank"
                  rel="noopener noreferrer">
                  {t(configMessages.terms)}
                </Link>
              )
            })}
            title=""
          />
        )}
      </div>
      <div className="ConversationalSearch__preview">
        <SkillSettingsPreviewHeader
          heading={t(messages.whatDifferent)}
          description={
            <>
              <div className="ConversationalSearch__preview-description">
                {t(messages.whatDifferentDescription)}
              </div>
              <div>{t(messages.whatDifferentDescription2)}</div>
            </>
          }
        />
        {previewPage === 0 && (
          <SampleConversationWidget heading={t(messages.defaultSearchHeader)}>
            <BotMessage>{t(messages.botMessage1)}</BotMessage>
            <ClientMessage>{t(messages.clientMessage1)}</ClientMessage>
            <BotMessage>{t(messages.botMessage2)}</BotMessage>
            <div className="ConversationalSearch__preview-search">
              <div>{t(messages.previewSearchMessage)}</div>
              <div className="ConversationalSearch__preview-search-link">
                {t(messages.previewSearchMessageLink)}
              </div>
            </div>
          </SampleConversationWidget>
        )}
        {previewPage === 1 && (
          <SampleConversationWidget heading={t(messages.convoSearchHeader)}>
            <BotMessage>{t(messages.botMessage1)}</BotMessage>
            <ClientMessage>{t(messages.clientMessage1)}</ClientMessage>
            <BotMessage bubbleText={t(messages.annotation1)}>
              {t(messages.botMessage3)}
            </BotMessage>
          </SampleConversationWidget>
        )}
        <PaginationNav
          className="ConversationalSearch__page-nav"
          onChange={setPreviewPage}
          totalItems={2}
        />
      </div>
    </div>
  );
};
